import React, { useEffect, useState } from 'react';
import MaterialTable, { MTableActions, MTableBody } from 'material-table';
import { NavLink } from 'react-router-dom';
import {
  Alert, Badge, Button, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import Send from '@material-ui/icons/Send';
import { History } from '@material-ui/icons';
import Delete from '@material-ui/icons/Delete';
import {
  stepScroll, styledTable, styledTableHeading, tableIcons, years,
} from '../../helpers';
import {
  localization, modifyDefaultOptions, options, tableIconsModified,
} from '../../helpers/table';

import YearSelect from '../common/YearSelect';
import StatusBadge from '../common/StatusBadge';

import {
  isAdmin, isPolicyAdmin, isPortfolioAdmin, isProjectAdmin, isStratAdmin,
} from '../../services/rolesSvc';
import { dateRenderer, floatRenderer } from '../../services/formatSvc';
import useUserContext from '../../hooks/useUserContext';
import useToast from '../../hooks/useToast';
import useAPI from '../../hooks/useAPI';

import { getBuyRemainders } from '../../pages/budget/helpers/remainders';
import { getFilterLookups, mapBuys } from './BuysRegisterHelper';
import FilterMultiSelect from '../table/FilterMultiSelect';
import BuysRegisterTableFooter from './BuysRegisterTableFooter';
import BuyVersionHistoryModal from '../../pages/budget/BuyVersionHistoryModal';
import ConfirmModal from '../common/ConfirmModal';
import { state as BST, status as BS } from '../../helpers/buy';
import PopoverElement from '../common/PopoverElement';
import LoadingTable from '../table/LoadingTable';
import {status as PS} from "../../helpers/passport";
import { getPathType } from "../../helpers/typeProject";

export default function BuysRegisterTab({
  stratId, originId, projectId, portfolioId, projectStatus, proj
}) {
  const userCtx = useUserContext();
  const toast = useToast();
  const api = useAPI();
  const [year, setYear] = useState(new Date().getFullYear());
  const handleYearChange = async (val) => {
    setYear(val.value);
    await getData(val.value);
  };

  // #region Perms
  // simplified role model
  // const canEdit = () =>  isProjectAdmin(userCtx, originId) || isPortfolioAdmin(userCtx, portfolioId) || isStratAdmin(userCtx, strategyId) || isAdmin(userCtx);
  const canEdit = () => ((isProjectAdmin(userCtx, originId) || isPortfolioAdmin(userCtx, portfolioId) || isStratAdmin(userCtx,stratId)) && ![PS.closed].includes(projectStatus) || isAdmin(userCtx))
  const canSendTo1S = (status, state) => isAdmin(userCtx) && status === 'approved' && !state;
  const canDelete = (buy) => (isProjectAdmin(userCtx, buy.projId) || isPortfolioAdmin(userCtx, portfolioId) || isStratAdmin(userCtx, stratId) || isAdmin(userCtx))
        && buy.version === 1 && buy.status === BS.notApproved && [BST.onProj, BST.onProjReturned].includes(buy.state);
  // #endregion

  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [buys, setBuys] = useState([]);
  const [remainders, setRemainders] = useState([]);
  const [buyHistoryModal, setBuyHistoryModal] = useState(null);
  const [isModalDeleteBuy, setIsModalDeleteBuy] = useState({ show: false, value: null });
  const [filterLookups, setFilterLookups] = useState({});
  const [canAdd, setCanAdd] = useState(false);

  // фильтры по руководителям и мероприятиям
  // const [headFilterLookup, setHeadFilterLookup] = useState({});
  // const [letterFilterLookup, setLetterFilterLookup] = useState({});

  useEffect(async () => {
    await getData(year); // первичный запрос данных с фильтром по году по умолчанию
  }, []);

  const getData = async (selectedYear) => {
    try {
      const result = await api.get(`/buys?year=${selectedYear}&strategyId=${stratId ?? 0}&projId=${originId ?? 0}&portfolioId=${!originId && portfolioId ? portfolioId : 0}`);
      if(result.error){
          throw new Error('Произошла ошибка')
      }
      setIsLoaded(true);
      const mappedBuys = mapBuys(result.buys);
      setBuys(mappedBuys);
      const newFilterLookups = getFilterLookups(mappedBuys);
      setFilterLookups(newFilterLookups);
      setCanAdd(originId && result.hasPlans && canEdit());

      // фильтр из имеющихся руководителей
      // setHeadFilterLookup(arrayToLookup(result.filter(r => r.head).map(r => r.head), 'displayName', 'displayName'));

      // выбираем проекты, где есть letters (filter), берем только это поле (letters),
      // делаем из массивов в массиве один плоский массив (flat()), берем только уникальные значения (uniq),
      // и делаем из этого объект нужной структуры для фильтра
      // todo: нужно ли подключить полный справочник мероприятий?
      // setLetterFilterLookup(Object.fromEntries(_.uniq(result.filter(r => r.letters).map(r => r.letters).flat()).map(l => [l, l])))
    } catch (err) {
      setIsLoaded(true);
      setError(err);
    }
  };

  const sendTo1S = async (id) => {
    const result = await api.post(`/buys/${id}/sendTo1S`, { id });
    if (result && !result.error) toast.success('Закупка успешно отправлена');
  };

  const handleDeleteBuy = async () => {
    const result = await api.del(`/buys/${isModalDeleteBuy.value}`, { id: isModalDeleteBuy.value });
    if (result && !result.error) {
      toast.success('Закупка успешно удалена');
      setBuys((prev) => prev.filter((v) => v.id !== isModalDeleteBuy.value));
    }
    setIsModalDeleteBuy({ show: false, value: null });
  };

  useEffect(() => {
    async function fetchRemainders() {
      // превышения остатков показываем только для руководителя проекта и ПО
      if (originId && (isProjectAdmin(userCtx, originId) || isAdmin(userCtx))) {
        const rems = await getBuyRemainders(year, originId, null);
        if (rems.error) toast.error(rems.error);
        if (rems.values) setRemainders(rems.values);
      }
    }
    fetchRemainders();
  }, []);

  // #region Lookups for filters
  /** Превращает массив объектов в объект формата { значение: заголовок },
     * который требуется для поля lookup в параметрах столбца
     */
  const arrayToLookup = (values, valueField, titleField) => (values && values.length > 0
    ? Object.fromEntries(values.map((v) => [v[valueField], v[titleField || valueField]]))
    : {});

  const arrayToLookupSortAlph = (values, valueField, titleField) => (values && values.length > 0
    ? Object.fromEntries(
      values
        .sort((a, b) => (a.displayName === b.displayName ? 0 : a.displayName < b.displayName ? -1 : 1))
        .map((v) => [v[valueField], v[titleField || valueField]]),
    )
    : {});

  // todo:
  // фильтр по соглашениям
  const [agreements, setAgreements] = useState([]);
  useEffect(() => {
    api.get('/mng/agreements').then((res) => setAgreements(res));
  }, []);

  // по кодам БК
  const [bcCodes, setBcCodes] = useState([]);
  useEffect(() => {
    api.get('/mng/bcCodes').then((res) => setBcCodes(res));
  }, []);

  // по типу финансирования
  const [funding, setFunding] = useState([]);
  useEffect(() => {
    api.get('/mng/funding').then((res) => setFunding(funding));
  }, []);

  const [statuses, setStatuses] = useState([]);
  useEffect(() => {
    api.get('/mng/statuses/buys').then((res) => setStatuses(res));
  }, []);
  const [states, setStates] = useState([]);
  useEffect(() => {
    api.get('/mng/states/buys').then((res) => setStates(res));
  }, []);
  // #endregion

  const getSourceTitle = (id, type) => {
    switch (type) {
      case 'agreements':
        return (agreements?.find((a) => a.id === id) || {}).num;
      case 'bcCodes':
        return (bcCodes?.find((a) => a.id === id) || {}).fullCode;
      case 'funding':
        return (funding?.find((a) => a.id === id) || {}).name;
      default:
        return id;
    }
  };

  const getBuyLink = (rowData = null) => {
    let link = '';
    const idParam = rowData?.id ?? 'new';
    const projParam = rowData?.projId ?? originId;
    const portfParam = rowData?.portfId ?? portfolioId;
    const strategyParam = rowData?.strategyId ?? stratId;
    const strategyType = rowData?.proj?.strategy?.type ?? proj?.strategy?.type;
    if (strategyParam) {
      link = `/${getPathType(strategyType)}/${strategyParam}${portfParam ? `/portfolios/${portfParam}` : ''}/projs/${projParam}/buys/${idParam}`;
    }
    return link;
  };

  if (error) {
    return (
      <div style={{ color: 'red', background: 'white' }}>
        Ошибка:
        {error.message}
      </div>
    );
  }

  return (
    <div className="tab-subproject-register" style={{ color: 'initial' }}>
      <div style={styledContainer}>
        <div>
          <YearSelect year={year} years={years} onChange={handleYearChange} />
        </div>
        {canAdd && (
        <div style={{ marginLeft: 16 }}>
          <NavLink to={() => getBuyLink()}>
            <Button variant="primary">Добавить закупку</Button>
          </NavLink>
        </div>
        )}
        {remainders && remainders.length > 0 && (
        <OverlayTrigger
          placement="right"
          overlay={
            <Tooltip id="tooltip-remainder">
              {remainders}
            </Tooltip>
          }
        >
          <Badge
            pill
            bg="danger"
            style={{
              display: 'block', marginLeft: '16px', marginTop: '7px', height: '20px',
            }}
          >
            !
          </Badge>
        </OverlayTrigger>
        )}
      </div>
      <LoadingTable spinnerStyle={{ marginTop: '100px' }} loadingObject={{ isLoaded }}>
        <div style={styledTable}>
          <MaterialTable
            title=""
            options={modifyDefaultOptions({ maxBodyHeight: '75vh' })}
            icons={tableIcons}
            localization={localization}
            actions={[
              (rowData) => ({
                disabled: !canSendTo1S(rowData.status, rowData.state),
                hidden: !isAdmin(userCtx),
                icon: Send,
                tooltip: 'Отправить в 1С',
                onClick: (event, row) => sendTo1S(row.id),
              }),
              (rowData) => ({
                custom: true,
                icon: () => <History style={{ color: '#0D6EFD' }} />,
                tooltip: 'История версий',
                onClick: (e, data) => {
                  setBuyHistoryModal({
                    id: rowData.id,
                    num: rowData.num,
                    version: rowData.version,
                    projId: rowData.projId,
                    stratId,
                  });
                },
              }),
              (rowData) => ({
                disabled: !canDelete(rowData),
                hidden: !isProjectAdmin(userCtx, rowData.projId) && !canEdit(),
                icon: ({ disabled }) => <Delete style={{ color: !disabled ? '#dc3545' : undefined }} />,
                tooltip: 'Удалить',
                onClick: (event, row) => setIsModalDeleteBuy({ show: true, value: rowData.id }),
              }),
            ]}
            components={{
              Body: (_props) => (
                <>
                  <MTableBody {..._props} />
                  <BuysRegisterTableFooter {..._props} />
                </>
              ),
            }}
            columns={[
              {
                title: 'Проект',
                field: 'projName',
                hidden: !!projectId,
                cellStyle: { minWidth: '300px' },
                filterCellStyle: { maxWidth: '300px' },
                lookup: filterLookups.projects,
                filterComponent: FilterMultiSelect,
              },
              {
                title: 'Руководитель проекта',
                field: 'projHead.displayName',
                hidden: !!projectId,
              },
              {
                title: <div style={{ width: '40px' }}>№ закупки</div>,
                field: 'num',
              },
              {
                title: 'Наименование',
                field: 'name',
                cellStyle: { minWidth: '400px' },
                filterCellStyle: { maxWidth: '400px' },
                render: (rowData) => (
                  <NavLink to={() => getBuyLink(rowData)}>
                    {rowData.name}
                  </NavLink>
                ),
                lookup: filterLookups.names,
                filterComponent: FilterMultiSelect,
              },
              {
                title: 'Версия',
                field: 'version',
                render: (rowData) => (
                  <>
                    {rowData.version}
                    {rowData.hasRecentDeclined
                      && (
                        <PopoverElement tooltip="Последняя версия закупки была отклонена. Отклоненную версию можно посмотреть в истории">
                          <Badge pill bg="danger" style={{ marginLeft: '5px' }}>!</Badge>
                        </PopoverElement>
                      )}
                  </>
                ),
                lookup: filterLookups.version,
                filterComponent: FilterMultiSelect,
              },
              {
                title: 'Статус',
                field: 'status',
                render: (rowData) => <StatusBadge type="buy" status={rowData.status} width={100} />,
                lookup: filterLookups.status,
                filterComponent: FilterMultiSelect,
              },
              {
                title: 'Состояние',
                field: 'state',
                render: (rowData) => 
                  <StatusBadge 
                    type="buyState" 
                    status = {
                      rowData.proj?.strategy?.type && ['onStrat', 'onStratReturned'].includes(rowData.state)
                        ? rowData.state.replace('Strat', 
                            rowData.proj.strategy.type.charAt(0).toUpperCase() + rowData.proj.strategy.type.slice(1))
                        : rowData.state
                    }
                    width={150}
                  />,
                lookup: filterLookups.state,
                filterComponent: FilterMultiSelect,
              },
              {
                title: 'Оценочная стоимость',
                field: 'assessedValue',
                cellStyle: { minWidth: '200px' },
                render: (rowData) => floatRenderer(rowData.assessedValue),
                aggregated: true,
              },
              {
                title: '№ соглашения / КБК (программная (непрограммной) статья) / КБК (раздела, подраздела) / Тип',
                cellStyle: { minWidth: '220px' },
                render: (rowData) => (
                  <>
                    { getSourceTitle(rowData.agreementId, 'agreements') }
                    <br />
                    { getSourceTitle(rowData.bcCodeId, 'bcCodes') }
                    <br />
                    { getSourceTitle(rowData.fundingId, 'funding') }
                  </>
                ),
              },
              {
                title: '№ протокола',
                field: 'protocolNum',
              },
              {
                title: 'Документы',
                field: 'docCount',
              },
            ]}
            data={buys}
          />
        </div>
      </LoadingTable>
      { buyHistoryModal && <BuyVersionHistoryModal buy={buyHistoryModal} onClose={() => setBuyHistoryModal(null)} />}
      <ConfirmModal
        show={isModalDeleteBuy.show}
        onClose={() => setIsModalDeleteBuy({ show: false, value: null })}
        onConfirm={handleDeleteBuy}
        headerText="Удаление закупки"
        message="Закупка будет удалена, данное действие нельзя отменить. Удалить закупку?"
      />
    </div>
  );
}

const styledContainer = {
  display: 'flex',
  zIndex: 11,
  position: 'relative',
  top: 84,
  width: 670,
  marginTop: -90,
};
